// Environment capture for the data spec: browser, OS, device class, timezone,
// locale, and a best-effort country. No precise geolocation — country only, and
// only what the browser already exposes (timezone) unless a lightweight IP-geo
// lookup succeeds. This keeps GDPR exposure low for the commercial-pilot phase.

import {
  isChrome,
  isMobile,
  isAndroid,
  isIOS,
  isSafari,
  isFirefox,
  browserName,
  osName,
} from 'react-device-detect'

export type CaptureEnv = {
  browserName: string
  osName: string
  isChrome: boolean
  isMobile: boolean
  isAndroid: boolean
  deviceClass: 'chrome-desktop' | 'chrome-android' | 'other'
  timezone: string
  locale: string
  country: string | null // best-effort, may stay null
  userAgent: string
  speechSupported: boolean
}

export const speechRecognitionSupported = (): boolean =>
  typeof window !== 'undefined' &&
  Boolean((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition)

// The brief: Chrome desktop or Chrome Android only. Everything else (Safari,
// Firefox, iOS) should be shown the "use Chrome" message rather than degrade.
export const isSupportedBrowser = (): boolean => {
  if (isIOS) return false // iOS Safari & all iOS browsers lack reliable Web Speech
  if (isSafari || isFirefox) return false
  if (!isChrome) return false
  return speechRecognitionSupported()
}

export const browserGateReason = (): 'ok' | 'ios' | 'safari' | 'firefox' | 'no-speech' | 'other' => {
  if (isIOS) return 'ios'
  if (isSafari) return 'safari'
  if (isFirefox) return 'firefox'
  if (!isChrome) return 'other'
  if (!speechRecognitionSupported()) return 'no-speech'
  return 'ok'
}

/**
 * True when we are running inside a native app's embedded WebView rather than a real browser.
 *
 * WHY THIS EXISTS. The nerdle app claims nerdlegame.com/* on both platforms, so recruitment
 * links open inside its WebView — which has no microphone capability (no RECORD_AUDIO on
 * Android, no NSMicrophoneUsageDescription on iOS). Android shows our mic-error screen; iOS is
 * killed outright by the OS. Cost before this check existed: 246 consented sessions, 48 people,
 * zero traces, every day from 2026-07-13.
 *
 * THIS CANNOT BE A CAPABILITY CHECK. Measured in the app on iOS 18.7, 2026-09-09:
 * `navigator.mediaDevices.getUserMedia` is PRESENT, `AudioContext` is PRESENT, `WebSocket` is
 * PRESENT — and calling getUserMedia still kills the app. Feature detection will always say
 * yes. Detecting the WebView is the only route.
 *
 * `display-mode: standalone` is NOT usable either — it reported **false** inside the app.
 *
 * SO IT IS USER-AGENT SHAPE, measured on the same device:
 *   in-app  Mozilla/5.0 (iPhone; …) AppleWebKit/605.1.15 (KHTML, like Gecko) Mobile/15E148
 *   Safari  Mozilla/5.0 (iPhone; …) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/26.6.1 Mobile/15E148 Safari/604.1
 * Android WebView self-identifies with a "; wv)" token. iOS WKWebView does not, and the nerdle
 * app sets no custom UA token, so it is detectable only by the ABSENCE of both `Version/` and
 * `Safari/`, which real Mobile Safari always carries.
 *
 * This deliberately also matches OTHER apps' iOS WebViews (in-app browsers in mail clients and
 * so on). That is correct rather than incidental: none of them can record either.
 */
export const isInAppWebView = (): boolean => {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  if (/;\s*wv[);]/.test(ua)) return true // Android WebView, self-identified
  if (isIOS && !/Version\//.test(ua) && !/Safari\//.test(ua)) return true // iOS WKWebView
  return false
}

const deviceClass = (): CaptureEnv['deviceClass'] => {
  if (isChrome && isAndroid) return 'chrome-android'
  if (isChrome && !isMobile) return 'chrome-desktop'
  return 'other'
}

export const captureEnv = (): CaptureEnv => {
  let timezone = ''
  let locale = ''
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch {
    timezone = ''
  }
  locale = (typeof navigator !== 'undefined' && (navigator.language || (navigator as any).userLanguage)) || ''

  return {
    browserName,
    osName,
    isChrome,
    isMobile,
    isAndroid,
    deviceClass: deviceClass(),
    timezone,
    locale,
    country: null, // populated later by enrichCountry() if the lookup succeeds
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    speechSupported: speechRecognitionSupported(),
  }
}

// Best-effort, country-only IP geo. Fails silently (returns null) — we never block
// the experience on it. Uses a free, no-key endpoint; if it's unavailable the
// timezone in CaptureEnv is enough to infer rough geography offline.
export const enrichCountry = async (): Promise<string | null> => {
  try {
    const res = await fetch('https://ipapi.co/country/', { method: 'GET' })
    if (!res.ok) return null
    const text = (await res.text()).trim()
    // Expect a 2-letter ISO country code; guard against error pages.
    return /^[A-Z]{2}$/.test(text) ? text : null
  } catch {
    return null
  }
}
